import * as Xstate from "@xstate/react";
import React, { FC, useEffect, useMemo } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { GlobalContext } from "../../../app/stateMachines/GlobalContext";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { useAppDispatch, useAppSelector } from "../../../redux/redux";
import { getUser } from "../../../services/AppService";
import { useGetOrgQuery } from "../../../services/OrgService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetFirmsWorksCountsQuery } from "../../../slices/WorkSlice";
import { CustomLink } from "../../../support/CustomLink";
import { GetPageTitle } from "../../../support/ScrollToTop";
import { HomeDashboard } from "../dashboard/HomeDashboard";
import HomeTeamWork from "../HomeTeamWork";
import { HomeFirmWorks } from "./HomeFirmWorks";
import { HomeMyWorks } from "./HomeMyWorks";
import { HomeUserWorks } from "./HomeUserWorks";

const VIEW_FIRM_WORK_PERMISSION = "4ef8ede7-80d5-43a4-9ad9-a9e08dbdb7c0";

export const HomeView: FC = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  // Home itself is the V50 dashboard; the older team/firm lists stay reachable.
  const isDashboard = /^\/home\/?$/.test(pathname);
  const orgId = getLinkedFirm()?.orgId;

  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const userInfoByEmail = userInfoState.context.userInfoByEmail;

  const user = useAppSelector((state) => state.appReducer.user);
  const isLoadingUser = useAppSelector(
    (state) => state.appReducer.isLoadingUser,
  );

  const { data: org, isLoading: isLoadingOrg } = useGetOrgQuery(orgId || "", {
    skip: !orgId,
  });

  useEffect(() => {
    document.title = GetPageTitle("Home");
  }, []);

  useEffect(() => {
    if (!userInfoByEmail?.id || !userInfoByEmail?.org?.id) return;

    dispatch(
      getUser({
        userId: String(userInfoByEmail.id),
        orgId: String(userInfoByEmail.org.id),
      }),
    );
  }, [dispatch, userInfoByEmail?.id, userInfoByEmail?.org?.id]);

  const canViewFirmWork = useMemo(() => {
    return (
      user?.userRoleEnum?.userRole === "Admin" ||
      !!user?.permissionIds?.includes(VIEW_FIRM_WORK_PERMISSION)
    );
  }, [user]);

  const { data: myCounts } = useGetFirmsWorksCountsQuery(
    {
      orgId: orgId || "",
      userId: user?.id || "",
      isMyWork: true,
    },
    {
      skip: !orgId || !user?.id,
    },
  );

  const { data: firmCounts } = useGetFirmsWorksCountsQuery(
    {
      orgId: orgId || "",
      userId: user?.id || "",
      isMyWork: false,
    },
    {
      skip: !orgId || !user?.id || !canViewFirmWork,
    },
  );

  if (isLoadingOrg || isLoadingUser) {
    return <LottieLoading />;
  }

  if (isDashboard) {
    return <HomeDashboard />;
  }

  if (!org) {
    return null;
  }

  return (
    <div>
      <div className={"mb-6 text-[24px] font-semibold"}>{org.legalName}</div>

      <div className={"mb-6 flex items-center gap-8"}>
        <CustomLink to={"/home/my-work"} count={myCounts?.allOpen?.total || 0}>
          {"My work"}
        </CustomLink>

        <CustomLink
          to={"/home/firm-work"}
          count={firmCounts?.allOpen?.total || 0}>
          {"Firm's work"}
        </CustomLink>

        <CustomLink to={"/home/team-work"}>{"Team's work"}</CustomLink>
      </div>

      <Routes>
        <Route path={"my-work"} element={<HomeMyWorks />} />
        <Route
          path={"firm-work"}
          element={
            canViewFirmWork ? (
              <HomeFirmWorks />
            ) : (
              <div
                className={
                  "flex h-[75vh] items-center justify-center text-[42px] font-bold text-gray-400"
                }>
                You don’t have access to this page
              </div>
            )
          }
        />
        <Route path={"team-work"} element={<HomeTeamWork />} />
        <Route path={"user-work/:userId"} element={<HomeUserWorks />} />
      </Routes>
    </div>
  );
};
